import React, { useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Alert,
  ProgressBar,
} from 'react-bootstrap';
import {
  BarChart,
  GraphUp,
  People,
  Prescription,
  CheckCircle,
  InfoCircle,
  Download,
  Printer,
  Calendar,
  FileMedical,

  Award,
} from 'react-bootstrap-icons';

// Données fictives étendues
const stats = {
  totalPatients: 124,
  totalPrescriptions: 287,
  consultationsByMonth: [
    { month: 'Jan 2025', count: 42 },
    { month: 'Fév 2025', count: 38 },
    { month: 'Mar 2025', count: 44 },
  ],
  prescriptionsByStatus: [
    { status: 'En attente', count: 32, color: 'warning' },
    { status: 'Délivrées', count: 255, color: 'success' },
  ],
  topMedications: [
    { name: 'Paracétamol 500mg', count: 78 },
    { name: 'Amoxicilline 250mg', count: 52 },
    { name: 'Ibuprofène 400mg', count: 41 },
    { name: 'Vitamine C', count: 35 },
  ],
  dailyAverage: 9.2,
  satisfaction: 94,
};

const Reports = () => {
  const [exporting, setExporting] = useState(false);
  const [printing, setPrinting] = useState(false);

  const handleExport = () => {
    setExporting(true);
    setTimeout(() => {
      alert('Export PDF (simulation)');
      setExporting(false);
    }, 800);
  };

  const handlePrint = () => {
    setPrinting(true);
    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 500);
  };

  // Calcul du taux de délivrance
  const deliveryRate = Math.round((stats.prescriptionsByStatus.find(s => s.status === 'Délivrées')?.count / stats.totalPrescriptions) * 100);

  // Cartes de statistiques (4 colonnes)
  const statsCards = [
    {
      title: 'Patients traités',
      value: stats.totalPatients,
      icon: People,
      color: '#4361ee',
      gradient: 'linear-gradient(135deg, #4361ee 0%, #3b2a9f 100%)',
      bg: 'white',
    },
    {
      title: 'Prescript. émises',
      value: stats.totalPrescriptions,
      icon: Prescription,
      color: '#10b981',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      bg: 'white',
    },
    {
      title: 'Taux délivrance',
      value: `${deliveryRate}%`,
      icon: CheckCircle,
      color: '#06b6d4',
      gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
      bg: 'white',
    },
    {
      title: 'Moy. journalière',
      value: stats.dailyAverage,
      icon: GraphUp,
      color: '#f59e0b',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      bg: 'white',
    },
  ];

  return (
    <div className="" style={{ fontFamily: "'Inter', sans-serif" }}>
      <div className="container-fluid">
        {/* En-tête */}
        <div className="mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <h4 className="main-title mb-0 d-flex align-items-center gap-2">
              <div className="bg-primary bg-opacity-10 rounded-circle p-2">
                <BarChart size={24} className="text-primary" />
              </div>
              Rapports d'activité
            </h4>
            <p className="text-muted mb-0 mt-1">
              Consultez les statistiques de votre activité médicale
            </p>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" size="sm" className="rounded-pill px-3" onClick={handleExport} disabled={exporting}>
              <Download size={14} className="me-1" />
              {exporting ? 'Export...' : 'Exporter'}
            </Button>
            <Button variant="outline-secondary" size="sm" className="rounded-pill px-3" onClick={handlePrint} disabled={printing}>
              <Printer size={14} className="me-1" />
              {printing ? 'Préparation...' : 'Imprimer'}
            </Button>
          </div>
        </div>

        {/* Cartes de statistiques */}
        <Row className="g-3 mb-4">
          {statsCards.map((card, idx) => (
            <Col md={6} lg={3} xs={6} key={idx}>
              <Card className="border-0 shadow-sm stats-card h-100" style={{ borderRadius: '16px', background: card.bg }}>
                <div className="card-gradient" style={{ background: card.gradient, height: '4px' }}></div>
                <Card.Body className="p-3 d-flex flex-column">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div>
                      <h6 className="text-muted mb-1 small fw-semibold text-uppercase" style={{ letterSpacing: '0.5px' }}>
                        {card.title}
                      </h6>
                      <h3 className="mb-0 fw-bold" style={{ fontSize: '1.8rem' }}>
                        {card.value}
                      </h3>
                    </div>
                    <div className="icon-container rounded-3 d-flex align-items-center justify-content-center" style={{ background: `${card.color}20`, width: '48px', height: '48px' }}>
                      <card.icon size={28} style={{ color: card.color }} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {/* Tableau des consultations par mois */}
        <Row className="mb-4">
          <Col md={12}>
            <Card className="border-0 shadow-sm bg-white" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
                <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                  <Calendar size={18} className="text-primary" />
                  Consultations par mois
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0 table-striped">
                    <thead className="bg-light">
                      <tr>
                        <th>Mois</th>
                        <th>Nombre de consultations</th>
                        <th>Évolution</th>
                        </tr>
                      </thead>
                    <tbody>
                      {stats.consultationsByMonth.map((item, idx) => {
                        const prev = idx > 0 ? stats.consultationsByMonth[idx-1].count : null;
                        const evolution = prev ? ((item.count - prev) / prev * 100).toFixed(0) : null;
                        const isUp = evolution && parseFloat(evolution) > 0;
                        return (
                          <tr key={idx}>
                            <td className="fw-medium">{item.month}</td>
                            <td>{item.count}</td>
                            <td>
                              {evolution && (
                                <Badge bg={isUp ? 'success' : 'danger'} className="rounded-pill px-3 py-2">
                                  <GraphUp size={12} className="me-1" />
                                  {isUp ? '+' : ''}{evolution}%
                                </Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Deuxième ligne : Prescriptions par statut + Médicaments les plus prescrits */}
        <Row className="g-4 mb-4">
          <Col md={6}>
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
                <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                  <Prescription size={18} className="text-success" />
                  Prescriptions par statut
                </h5>
              </Card.Header>
              <Card.Body className="p-4">
                {stats.prescriptionsByStatus.map((item, idx) => (
                  <div key={idx} className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <span className="small fw-medium">{item.status}</span>
                      <span className="small fw-medium">{item.count}</span>
                    </div>
                    <ProgressBar
                      now={(item.count / stats.totalPrescriptions) * 100}
                      variant={item.color}
                      className="rounded-pill"
                      style={{ height: '8px' }}
                    />
                    <div className="d-flex justify-content-between mt-1">
                      <small className="text-muted">
                        {Math.round((item.count / stats.totalPrescriptions) * 100)}% du total
                      </small>
                      <small className="text-muted">{item.count} prescriptions</small>
                    </div>
                  </div>
                ))}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px', overflow: 'hidden' }}>
              <Card.Header className="bg-white border-0 pt-4 pb-4 px-4">
                <h5 className="fw-semibold mb-0 d-flex align-items-center gap-2">
                  <FileMedical size={18} className="text-warning" />
                  Top 4 médicaments prescrits
                </h5>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th>Médicament</th>
                        <th>Nombre de prescriptions</th>
                        <th>%</th>
                        </tr>
                      </thead>
                    <tbody>
                      {stats.topMedications.map((med, idx) => (
                        <tr key={idx}>
                          <td className="fw-medium">{med.name}</td>
                          <td>{med.count}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <div className="progress flex-grow-1" style={{ height: '6px' }}>
                                <div
                                  className="progress-bar bg-primary"
                                  style={{ width: `${(med.count / stats.totalPrescriptions) * 100}%` }}
                                ></div>
                              </div>
                              <span className="small fw-medium">
                                {Math.round((med.count / stats.totalPrescriptions) * 100)}%
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Information */}
        <Row>
          <Col md={12}>
            <Alert variant="info" className="border-0 rounded-3 shadow-sm" style={{ background: 'linear-gradient(135deg, #e8f4fd 0%, #d9eaff 100%)' }}>
              <div className="d-flex align-items-center gap-3">
                <InfoCircle size={24} className="text-info" />
                <div>
                  <strong className="d-block mb-1">Rapports d'activité</strong>
                  <span className="small">
                    Ces données sont mises à jour en temps réel. Utilisez les boutons d’export pour sauvegarder vos rapports.
                  </span>
                </div>
              </div>
            </Alert>
          </Col>
        </Row>

        <style>{`
          .stats-card {
            position: relative;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
            border-radius: 16px;
            overflow: hidden;
          }
          .stats-card:hover {
            transform: translateY(-5px);
            box-shadow: 0 12px 24px rgba(0, 0, 0, 0.12) !important;
          }
          .card-gradient {
            transition: height 0.2s ease;
          }
          .stats-card:hover .card-gradient {
            height: 6px;
          }
          .icon-container {
            transition: transform 0.2s ease;
          }
          .stats-card:hover .icon-container {
            transform: scale(1.05);
          }
          .progress {
            background-color: #e9ecef;
          }
          .progress-bar {
            border-radius: 4px;
          }
          .table > :not(caption) > * > * {
            padding: 0.75rem 0.75rem;
            vertical-align: middle;
          }
        `}</style>
      </div>
    </div>
  );
};

export default Reports;